import styled from "@emotion/styled";
import { ErrorMessage } from "@hookform/error-message";
import { Stack } from "@mui/material";
import {
  useDenomination,
  useDenominationChurchesPublic,
  useDenominationHierarchyPublic
} from "features/denominations";
import { MuiTelInput } from "mui-tel-input";
import { useMemo } from "react";
import { Controller } from "react-hook-form";
import { useTranslation } from "react-i18next";

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 100%;
`;

const Label = styled.label`
  color: #5b5b5b;
  font-size: 12px;
  font-weight: 500;
`;

const Input = styled.input`
  border-radius: 4px;
  border: 0.73px solid #b3b3b3;
  height: 40px;
  outline: none;
  padding: 8px;
`;

const Select = styled.select`
  border-radius: 4px;
  border: 0.73px solid #b3b3b3;
  height: 40px;
  outline: none;
  padding: 8px;
  font-family: inherit;
`;

const HelperText = styled.span`
  font-size: 12px;
  color: ${({ theme, error }) =>
    error ? theme.palette.error.main : "inherit"};
`;

export const EmployerInfoForm = ({
  register,
  errors,
  control,
  denominationId,
  setValue,
  watch
}) => {
  const { t } = useTranslation();

  const denominationQuery = useDenomination({
    denominationId,
    isPublic: true,
    config: {
      enabled: !!denominationId
    }
  });

  const denominationChurchesPublicQuery = useDenominationChurchesPublic({
    denominationId,
    config: {
      enabled: !!denominationId
    }
  });

  const denominationHierarchyPublicQuery = useDenominationHierarchyPublic({
    denominationId,
    config: {
      enabled: !!denominationId
    }
  });

  const churches = useMemo(() => {
    return (
      [
        ...(denominationQuery.data?.result
          ? [denominationQuery.data.result]
          : []),
        ...(denominationChurchesPublicQuery.data?.result || []),
        ...(denominationHierarchyPublicQuery.data?.result?.data || [])
      ] || []
    );
  }, [
    denominationChurchesPublicQuery.data,
    denominationHierarchyPublicQuery.data,
    denominationQuery.data
  ]);

  return (
    <Stack spacing={2}>
      <Field>
        <Label htmlFor="employer_name">
          {t("HR.AddEmployee.EmployerName")} (
          {t("churches.createChurch.churchName")})
        </Label>
        {denominationId ? (
          <Select
            id="employer_name"
            value={watch("employer_name")}
            onChange={e => {
              const selectedChurchName = e.target.value;

              const selectedChurch = churches.find(
                church =>
                  church?.name?.toLowerCase().trim() ===
                  selectedChurchName?.toLowerCase().trim()
              );

              setValue("employer_name", selectedChurchName || "", {
                shouldValidate: true
              });
              setValue(
                "employer_location",
                selectedChurch?.specific_location || "",
                { shouldValidate: true }
              );
              setValue(
                "employer_house_number",
                selectedChurch?.house_number || "",
                { shouldValidate: true }
              );
              setValue("employer_phone_number", selectedChurch?.phone || "", {
                shouldValidate: true
              });
            }}
          >
            <option value="" disabled>
              {t("denomination.reporting.selectChurch")}
            </option>
            {churches.map(church => (
              <option key={church?.id} value={church?.name}>
                {church?.name}
              </option>
            ))}
          </Select>
        ) : (
          <Input id="employer_name" {...register("employer_name")} />
        )}
        <ErrorMessage
          errors={errors}
          name="employer_name"
          render={({ message }) => <HelperText error>{message}</HelperText>}
        />
      </Field>
      <Field>
        <Label htmlFor="employer_location">
          {t("HR.AddEmployee.Location")}
        </Label>
        <Input id="employer_location" {...register("employer_location")} />
        <ErrorMessage
          errors={errors}
          name="employer_location"
          render={({ message }) => <HelperText error>{message}</HelperText>}
        />
      </Field>
      <Field>
        <Label htmlFor="employer_house_number">
          {t("HR.AddEmployee.HouseNumber")}
        </Label>
        <Input
          id="employer_house_number"
          {...register("employer_house_number")}
        />
        <ErrorMessage
          errors={errors}
          name="employer_house_number"
          render={({ message }) => <HelperText error>{message}</HelperText>}
        />
      </Field>
      <Field>
        <Label htmlFor="employer_phone_number">
          {t("HR.Adress.Phone Number")}
        </Label>
        <Controller
          name="employer_phone_number"
          control={control}
          render={({ field }) => (
            <MuiTelInput
              forceCallingCode
              defaultCountry="ET"
              disableFormatting
              size="small"
              inputProps={{
                maxLength: 13
              }}
              {...field}
              sx={{
                "& .MuiInputBase-root": {
                  backgroundColor: "rgb(255, 255, 255)"
                },
                "& fieldset": {
                  borderWidth: "0.73px",
                  borderColor: "#b3b3b3"
                }
              }}
            />
          )}
        />
        <ErrorMessage
          errors={errors}
          name="employer_phone_number"
          render={({ message }) => <HelperText error>{message}</HelperText>}
        />
      </Field>
    </Stack>
  );
};
